import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  TextField,
  Typography,
  Link as MuiLink,
} from '@mui/material'
import { FormEvent, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { postResetPassword } from 'src/api/authentication'

interface ResetPasswordPageProps {
  theme: any
}

const ResetPasswordPage = ({ theme }: ResetPasswordPageProps) => {
  const location = useLocation()
  const [token, setToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    // Extract token from URL query params
    const queryParams = new URLSearchParams(location.search)
    const tokenParam = queryParams.get('token')
    if (tokenParam) {
      setToken(tokenParam)
    }
  }, [location])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate inputs
    if (!token) {
      setError('Invalid reset token. Please check your reset link.')
      return
    }

    if (!newPassword) {
      setError('Please enter a new password')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await postResetPassword(token, newPassword)
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || 'An error occurred while resetting the password')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!token && !isSubmitting && !success) {
    return (
      <Box sx={{ flex: 1, height: '100%' }}>
        <Container maxWidth="sm" sx={{ pt: 15 }}>
          <Card sx={{ p: 5 }}>
            <Typography
              variant="h1"
              color="text.primary"
              component="h1"
              gutterBottom
              fontWeight={500}
            >
              Reset Password
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              Invalid or missing reset token. Please check your reset link or request a new one.
            </Alert>
            <Box sx={{ mt: 3 }}>
              <MuiLink component={Link} to="/forgot-password" underline="hover">
                Request a new password reset
              </MuiLink>
            </Box>
          </Card>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, height: '100%' }}>
      <Container maxWidth="sm" sx={{ pt: 15 }}>
        <Card sx={{ p: 5 }}>
          <Typography
            variant="h1"
            color="text.primary"
            component="h1"
            gutterBottom
            fontWeight={500}
          >
            Reset Password
          </Typography>

          {success ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                Your password has been reset successfully.
              </Alert>
              <Box sx={{ mt: 3 }}>
                <MuiLink component={Link} to="/" underline="hover">
                  Return to login
                </MuiLink>
              </Box>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography
                variant="h4"
                component="div"
                color="text.secondary"
                fontSize={20}
                fontWeight={300}
                sx={{ mb: 3 }}
              >
                Enter your new password below.
              </Typography>

              <TextField
                required
                id="newPassword"
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                sx={{ mt: 3, mb: 2 }}
              />

              <TextField
                required
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert color="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {isSubmitting ? (
                <Box textAlign="center">
                  <CircularProgress/>
                </Box>
              ) : (
                <Button
                  type="submit"
                  size="large"
                  color="primary"
                  variant="contained"
                  fullWidth
                  disabled={!newPassword || !confirmPassword}
                  sx={{ mt: 3, height: '60px' }}
                >
                  Reset Password
                </Button>
              )}
            </Box>
          )}
        </Card>
      </Container>
    </Box>
  )
}

export default ResetPasswordPage 